import * as React from "react";
import { Form, useFormApi } from "informed";
import DocumentTitle from "../../misc/components/DocumentTitle";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { Link, useParams } from "react-router-dom";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { Authorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { useReferenceContextProvider } from "../../Core/ReferenceContextProvider";
import { PopulationGroupFormBody, PopulationGroupFormModel } from "./PopulationGroupFormBody";
import { ActionButtons } from "../../misc/ActionButtons";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";

export const EditPopulationGroupForm = ({id, errorAlert, setErrorAlert})  => {
    const { countries, study } = useCurrentStudyConfigurationContext();
    const formApi = useFormApi<PopulationGroupFormModel>();
    const [successAlert, setSuccessAlert] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);
    const [warningAlert, setWarningAlert] = React.useState<string>(null);

    const client = useWebPortalApiClient();

    const onSubmit = async () => {
        setSubmitting(true);
        setSuccessAlert(false);
        setWarningAlert(null);
        setErrorAlert(null);

        formApi.validate();
        const state = formApi.getState();

        if (state.invalid) {
            setSuccessAlert(false);
            setSubmitting(false);
            return setErrorAlert("Errors exist on the page");
        }

        try {
            const popGroup = await client.client.patchPopulationGroup(
                {
                    name: state.values.Name,
                    race: state.values.CoreRace,
                    predictedSetId: state.values.PredictedSet,
                    correctionFactor: state.values.CorrectionFactor,
                    isDisabled: state.values.Disabled,
                    studyId: study.id,
                }, id);

            setSuccessAlert(true);

        } catch (ex) {
            setErrorAlert(ex.message);
        }

        setSubmitting(false);
    };

    

    return (
        <>
            <Alert show={successAlert} category={AlertCategoryEnum.Success}>
                Saved successfully. Return to{" "}
                <Link to={`/studies/populationgroups/${study.id}`}>Population group list</Link>?
            </Alert>
            <Alert show={warningAlert !== null} category={AlertCategoryEnum.Warning}>
                {warningAlert}
            </Alert>
            <Alert show={errorAlert !== null} category={AlertCategoryEnum.Danger}>
                {errorAlert}
            </Alert>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Edit Population Group</h2>
                </div>
                <div className="col-md-6">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} returnToListRoute={`/studies/populationgroups/${study.id}`} automationIdPrefix="ActionsHeader" />
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div id="detailsPanel" className="panel panel-primary">
                        <div className="panel-heading">
                            <h4>Details</h4>
                        </div>
                        <div className="panel-body">
                            <PopulationGroupFormBody />
                        </div>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <ActionButtons submitting={submitting} onSaveClick={onSubmit} returnToListRoute={`/studies/populationgroups/${study.id}`} automationIdPrefix="ActionsFooter" />
                </div>
            </div>
        </>
    );
};

export const EditPopulationGroupScreen = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { id } = useParams<{ id: string }>();
    const [errorAlert, setErrorAlert] = React.useState<string>(null);
    const [populationGroupFromModel, setPopulationGroupFormModel] = React.useState<PopulationGroupFormModel>(null);

    const client = useWebPortalApiClient();
    React.useEffect(() => {

        const getFormState = async () => {
            try {
                const populationGroup = await client.client.getPopulationGroup(id, undefined);
                setPopulationGroupFormModel({
                    CoreRace : populationGroup.race,
                    CorrectionFactor : populationGroup.correctionFactor,
                    Name : populationGroup.name,
                    PredictedSet : populationGroup.predictedSetKey,
                    Disabled : populationGroup.isDisabled
                });
            } catch (e) {
                setErrorAlert(e.message);
            } 
        };
        getFormState();
    }, [id]);
    if (!populationGroupFromModel) return (<LoadingWithMessage message="Loading Population Group..." />);


    return (
        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.EditPopulationGroups]}>
            <DocumentTitle title="Edit Population Group | Vitalograph Web Portal">
                <Form noValidate initialValues={populationGroupFromModel}>
                    <EditPopulationGroupForm  id={id} setErrorAlert={setErrorAlert} errorAlert={errorAlert}/>
                </Form>
            </DocumentTitle>
        </Authorization>
    );
}
